import React, {useEffect, useState} from "react";
import {Button, Input, Modal, notification, Select} from "antd";
import {useNavigate, useParams} from "react-router-dom";
import {useAuth} from "oidc-react";
import {useJwt} from "react-jwt";
import {CloseOutlined} from "@ant-design/icons";
import SharedService from "../../../services/shared/service";

const {Option} = Select;
const {TextArea} = Input;

const RejectPopUp = ({isVisible, onClose, emailData}) => {
    const [statusOptions, setStatusOptions] = useState([]);
    const [selectedReason, setSelectedReason] = useState(null);
    const [notes, setNotes] = useState("");
    const [selectedValues, setSelectedValues] = useState([]);
    const [submitting, setSubmitting] = useState(false);

    const navigate = useNavigate();
    const {id} = useParams();
    const {userData, userManager} = useAuth();
    const token = userData?.access_token;
    const {decodedToken} = useJwt(token);
    const userEmail = decodedToken?.email;

    const refreshAccessToken = async () => {
        try {
            await userManager.signinSilent();
        } catch {
            /* noop */
        }
    };

    useEffect(() => {
        const refreshInterval = setInterval(refreshAccessToken, 600000);
        return () => clearInterval(refreshInterval);
    }, [userManager]);

    useEffect(() => {
        fetchStatusOptions();
    }, []);

    const fetchStatusOptions = async () => {
        try {
            const data = await SharedService.get({
                token,
                url: "reference/document/rejectreasons",
            });
            const sortedData = [...(data ?? [])].sort((a, b) => {
                if (a.value < b.value) return -1;
                if (a.value > b.value) return 1;
                return 0;
            });
            setStatusOptions(sortedData);
        } catch (error) {
            console.error(error);
        }
    };

    const resetForm = () => {
        setSelectedReason(null);
        setNotes("");
        setSelectedValues([]);
    };

    const handlePopupOk = async () => {
        if (submitting) return;
        setSubmitting(true);
        try {
            await SharedService.patch({
                token,
                url: "document",
                data: {
                    id,
                    status: "rejected",
                    comment: notes,
                    responseEmails: selectedValues,
                    rejectReasonId: selectedReason,
                    updatedBy: userEmail,
                },
            });
            resetForm();
            onClose();
            notification.success({message: "Invoice Rejected"});
            navigate("/invoice");
        } catch (error) {
            notification.error({message: "Failed to reject invoice"});
            console.error(error);
            onClose();
        } finally {
            setSubmitting(false);
        }
    };

    const handlePopupCancel = () => {
        resetForm();
        onClose();
    };

    const customTagRender = ({label, onClose: onTagClose}) => (
        <span
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "2px 8px",
                margin: "2px 4px 2px 0",
                background: "#F1F3F7",
                color: "#374152",
                borderRadius: 12,
                fontSize: 12,
            }}
        >
            <span>{label}</span>
            <CloseOutlined
                onClick={onTagClose}
                style={{fontSize: 10, color: "#7A849A", cursor: "pointer"}}
            />
        </span>
    );

    const isSubmitDisabled = !selectedReason || submitting;

    return (
        <Modal
            title="Reject Invoice"
            open={isVisible}
            onOk={handlePopupOk}
            onCancel={handlePopupCancel}
            closable={true}
            width={600}
            footer={[
                <Button key="cancel" onClick={handlePopupCancel} disabled={submitting}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    danger
                    onClick={handlePopupOk}
                    loading={submitting}
                    disabled={isSubmitDisabled}
                >
                    Reject Invoice
                </Button>,
            ]}
        >
            <div className="flex flex-col gap-4">
                <div>
                    <label className="block text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
                        Reason <span style={{color: "#A4231D"}}>*</span>
                    </label>
                    <Select
                        style={{width: "100%"}}
                        placeholder="Select a reason"
                        onChange={setSelectedReason}
                        value={selectedReason}
                    >
                        {statusOptions.map((option) => (
                            <Option key={option.id} value={option.id}>
                                {option.value}
                            </Option>
                        ))}
                    </Select>
                </div>

                <div>
                    <label className="block text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
                        Notes
                    </label>
                    <TextArea
                        autoSize={{minRows: 4, maxRows: 8}}
                        placeholder="Add context for the rejection (optional)"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>

                <div>
                    <label className="block text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide mb-1.5">
                        Notify by email
                    </label>
                    <Select
                        style={{width: "100%"}}
                        mode="multiple"
                        placeholder="Select recipients"
                        tagRender={customTagRender}
                        onChange={setSelectedValues}
                        value={selectedValues}
                        options={emailData}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default RejectPopUp;
